import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import VerificationInput from 'react-verification-input';
import user from 'stores/user';
import { getOnePieceServerApi } from 'gql-ops';
import { homePath } from 'constant/route-path';
import { useInviteCode } from 'hooks/useInvite';
import classNames from 'classnames';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import Button from 'components/Button';
import Modal from 'components/Modal';
import { autorun } from 'mobx';
import { GA_EVENT_ACTION, GA_EVENT_CATEGORY, trackEvent } from 'utils/googleAnalytics';
import { TYPE } from 'theme';

const InviteWrapper = styled.div`
    padding-top: 150px;
    min-height: 660px;
    height: calc(100vh - 447px);
    background-color: #121212;
    background-repeat: no-repeat;
    background-image: url(${require('assets/images/invite/bg.png')});
    background-size: 100%;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        padding-top: 120px;
        min-height: 0;
        height: 615px;
        // height: calc(100vh - 160px);
        background-image: url(${require('assets/images/invite/mobile-bg.png')});
        background-size: 100%;
    `};

    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        height: 595px;
    `};
`;

const InviteContent = styled.div`
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Header = styled.div`
    color: #fff;
    text-shadow: 0px 3.75px 11.25px rgba(0, 0, 0, 0.25);
    text-align: center;
    font-size: 56px;
    font-weight: 700;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        font-size: 40px;
        text-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
    `};
`;

const SubHeader = styled.div`
    margin: 10px 0 40px;
    color: #fff;
    font-size: 24px;
    font-weight: 400;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        margin: 20px 0 32px;
        font-size: 16px;
        text-shadow: 0px 5px 15px rgba(0, 0, 0, 0.25);
    `};
`;

const YourInviter = styled.div`
    margin-bottom: 0;
    padding: 10px 30px;
    border-radius: 24px;
    background: rgba(0, 0, 0, 0.5);
    font-size: 12px;
    font-weight: 700;
    color: #fff;
`;
const YourInviterLabel = styled.span`
    margin-right: 20px;
    color: rgba(255, 255, 255, 0.7);
`;

const BottomRow = styled.div`
    margin-top: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    width: 100% !important;
    max-width: 630px !important;
    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        max-width: 260px !important;;
    `};
    ${({ theme }) => theme.mediaWidth.upToSmall`
        flex-direction: column;
        max-width: 300px !important;;
        gap: 16px;
    `};
`;

const InviteCodeWrapper = styled.div`
    position: relative;
    padding-bottom: 40px;

    .vi__container {
        width: auto;
        height: 80px;
        gap: 30px;

        .vi__character {
            width: 80px;
            height: 80px;
            line-height: 76px;
            font-size: 24px;
            font-weight: 700;
            border: 2px solid #fff;
            border-radius: 16px;

            &.vi__character--selected {
                outline: 2px solid ${({ theme }) => theme.c.primary01};
                color: #000;
            }

            &.vi__character--inactive {
                border-color: lightgray;
            }
        }
    }

    &.invalid {
        .vi__container {
            .vi__character {
                border-color: #e72d2d;
            }
        }
    }

    ${({ theme }) => theme.mediaWidth.upToSmall`
        padding-bottom: 0;
        .vi__container {
            gap: 4px;

            .vi__character {
                width: 48px;
                height: 48px;
                line-height: 44px;
                border-radius: 8px;
            }
        }
    `};
    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        .vi__container .vi__character {
            width: 40px;
            height: 40px;
            line-height: 40px;
            border-radius: 8px;
        }
    `};
`;

const ConfirmButton = styled(Button)`
    font-size: 18px !important;
    font-weight: 400;
    border: none;
    outline: none;
    border-radius: 12px !important;
    width: 100% !important;
    max-width: 630px !important;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        max-width: 300px !important;;
    `};
    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        max-width: 260px !important;;
    `};
`;

const Skip = styled.div`
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #fff;
    font-weight: 400;
`;
const SkipButton = styled(Button).attrs({ type: 'unstyled', size: 'unset' })`
    margin-left: 8px;
    padding: 0;
    width: 48px;
    height: 24px;
    background: ${({ theme }) => theme.c.primary02};
    color: #fff;
    outline: none;
    border: none;
    cursor: pointer;
    border-radius: 12px;

    &:disabled {
        cursor: not-allowed;
    }
`;

const ModalContent = styled.div`
    position: relative;
    width: 330px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 90%;
    `};
`;

const SkipTitle = styled.div`
    width: 100%;
    height: 80px;
    line-height: 80px;
    font-size: 28px;
    font-weight: 400;
    text-align: center;
`;

const SkipText = styled.div`
    text-align: center;
    margin-top: 20px;
`;

const SkipModalButton = styled(Button)`
    margin-top: 40px;
    border: none;
    border-radius: 12px !important;
    width: 100%;
    height: 50px !important;
    font-size: 20px !important;
`;

const GradientLine = styled.div`
    height: 2px;
    width: 200px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 100px;
    `};
    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        width: 60px;
    `};
`;

const GradientLineRight = styled(GradientLine)`
    transform: rotate(180deg);
`;

const TitleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    width: 100% !important;
    max-width: 630px !important;
    margin-bottom: 50px;
`;

function maskAddress(address?: string) {
    if (!address) return '';

    return address.slice(0, 6) + '...' + address.slice(-4);
}

function Invite() {
    const location = useLocation();
    const history = useHistory();
    const { inviteCode: pathInviteCode } = useParams<{ inviteCode: string }>();
    const searchParams = new URLSearchParams(location.search);
    const [inviteCode, setInviteCode, inviter, invalidMsg] = useInviteCode(pathInviteCode || '');
    const [isSkipModalVisible, setSkipModalVisible] = useState(false);
    const closeSkipModal = useCallback(() => setSkipModalVisible(false), [setSkipModalVisible]);
    const { openConnectModal } = useConnectModal();
    const onConfirm = async () => {
        await getOnePieceServerApi(true).bindInviteCode({
            inviteCode,
            address: user.address,
        });
        await user.refreshOpsUserInfo();
    };
    const onSkip = useCallback(() => {
        if (!user.address) {
            openConnectModal?.();
        } else {
            setSkipModalVisible(true);
            trackEvent({
                category: GA_EVENT_CATEGORY.Onboarding,
                action: GA_EVENT_ACTION.click_skip_invite,
            });
        }
    }, [user.address]);
    const onSkipConfirm = useCallback(async () => {
        await getOnePieceServerApi(true).skipInviteBind({
            address: user.address,
        });
        await user.refreshOpsUserInfo();
    }, []);

    useEffect(
        () =>
            autorun(() => {
                if (user.opsUser?.skipInviteBind || user.opsUser?.invitedBy) {
                    history.push({
                        pathname: homePath,
                        search: location.search,
                    });
                }
            }),
        [],
    );

    // create opsUser
    useEffect(
        () =>
            autorun(async () => {
                if (user.opsUserFetched && !user.opsUser && user.address) {
                    await getOnePieceServerApi(true).createUser({
                        address: user.address,
                        input: {},
                    });
                    user.refreshOpsUserInfo();
                }
            }),
        [],
    );

    return (
        <>
            <InviteWrapper>
                <InviteContent>
                    <Header>GOAT One Piece Project</Header>
                    <SubHeader>Growth, Community, Rewards</SubHeader>
                    <TitleWrapper>
                        <GradientLine />
                        <TYPE.desc fontWeight={700} fontSize={24}>
                            Input Your Invitation Code
                        </TYPE.desc>
                        <GradientLineRight />
                    </TitleWrapper>
                    <InviteCodeWrapper className={classNames({ invalid: !!invalidMsg })}>
                        <VerificationInput length={6} placeholder="" value={inviteCode} onChange={setInviteCode} />
                    </InviteCodeWrapper>
                    <ConfirmButton
                        type="gradient"
                        size="small"
                        disabled={inviteCode.length < 6 || !user.address || !!invalidMsg || !inviter}
                        onClick={onConfirm}
                    >
                        Confirm
                    </ConfirmButton>
                    <BottomRow>
                        <YourInviter>
                            <YourInviterLabel>Your Inviter:</YourInviterLabel>
                            {maskAddress(inviter?.walletAddress)}
                        </YourInviter>
                        <Skip>
                            <span>Don&apos;t have invite code ?</span>
                            <SkipButton onClick={onSkip}>Skip</SkipButton>
                        </Skip>
                    </BottomRow>
                </InviteContent>
            </InviteWrapper>
            <Modal isOpen={isSkipModalVisible} onClose={closeSkipModal} shadowProps>
                <ModalContent>
                    <SkipTitle>No invite code?</SkipTitle>
                    <SkipText>
                        You can bind an invite code later in the dashboard or view your own invite code.
                    </SkipText>
                    <SkipModalButton onClick={onSkipConfirm} type="gradient">
                        Skip
                    </SkipModalButton>
                </ModalContent>
            </Modal>
        </>
    );
}

export default observer(Invite);
